import { useState, useEffect, useRef, type CSSProperties } from "react";
import { useBackRefs } from "../../hooks/use-back-refs";
import { CopyableCode } from "../primitives";

/**
 * Sprint 14g — generic type-the-name confirmation for an irreversible row delete
 * across cockpit panels (Tasks / Tasks / Routines / Pages / Prompts /
 * Memories / Files). Generalises Sprint 14a's ConfirmDeleteTableModal: same
 * type-the-name ceremony, but parametrised by entity kind and with a live
 * back-reference surface fetched via `get_back_references`. When references
 * exist, the owner is shown them and confirming passes `force: true` (the
 * informed override) — otherwise the worker would block the delete.
 *
 * Activity has its own variant (ConfirmDeleteActivityModal) with a reason field.
 */
export interface ConfirmDeleteRowModalProps {
  open: boolean;
  onClose: () => void;
  /** Human label for the kind, e.g. "page", "routine", "file". */
  entityKind: string;
  /** Display name typed to confirm. Falls back to the id when blank. */
  entityName: string;
  entityId: string;
  /** Back-ref scan kind (defaults to entityKind). Use "node" for library nodes. */
  backRefKind?: string;
  /** Performs the delete. `force` is true when references exist (informed override). */
  onConfirm: (opts: { force: boolean }) => Promise<void>;
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
  fontFamily: "var(--font-sans)",
};
const modal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  padding: 24,
  width: 460,
  maxWidth: "90vw",
};
const inputStyle: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};
const btnBase: CSSProperties = {
  fontSize: 13,
  padding: "8px 16px",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  border: "none",
};

export function ConfirmDeleteRowModal({
  open,
  onClose,
  entityKind,
  entityName,
  entityId,
  backRefKind,
  onConfirm,
}: ConfirmDeleteRowModalProps) {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const backRefs = useBackRefs(backRefKind ?? entityKind, entityId, open);

  useEffect(() => {
    if (open) {
      setTyped("");
      setError(null);
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const target = entityName.trim() || entityId;
  const confirmed = typed.trim() === target;
  const refCount = backRefs.data?.count ?? 0;

  async function handleDelete() {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ force: refCount > 0 });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={`Delete ${entityKind}`} style={modal}>
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 8px 0", color: "var(--color-text-primary)" }}>
          Delete this {entityKind}?
        </h2>
        <div style={{ fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
          This permanently deletes{" "}
          <strong style={{ color: "var(--color-text-primary)" }}>{target}</strong>. This cannot be undone — use
          archive instead if you might want it back.
        </div>

        {/* Back-reference surface — Sprint 14g. */}
        {backRefs.isLoading ? (
          <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginBottom: 16 }}>
            Checking references…
          </div>
        ) : refCount > 0 ? (
          <div
            style={{
              fontSize: 13,
              color: "var(--color-text-secondary)",
              lineHeight: 1.5,
              marginBottom: 16,
              padding: "10px 12px",
              borderRadius: "var(--border-radius-md)",
              background: "var(--c-amber-50, rgba(245, 158, 11, 0.08))",
              border: "0.5px solid var(--c-amber-200, rgba(245, 158, 11, 0.3))",
            }}
          >
            <strong style={{ color: "var(--color-text-primary)" }}>
              Referenced by {refCount} {refCount === 1 ? "item" : "items"}.
            </strong>{" "}
            Deleting will remove it anyway:
            <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
              {backRefs.data!.refs.slice(0, 5).map((r) => (
                <li key={`${r.kind}:${r.id}`}>
                  <span style={{ color: "var(--color-text-primary)" }}>{r.name || r.id}</span>{" "}
                  <span style={{ color: "var(--color-text-tertiary)" }}>({r.kind})</span>
                </li>
              ))}
              {refCount > 5 && <li>and {refCount - 5} more…</li>}
            </ul>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginBottom: 16 }}>
            No back-references found — safe to delete.
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 6 }}>
          Type <CopyableCode text={target} label="Copy name" /> to confirm
        </div>
        <input
          ref={inputRef}
          type="text"
          style={inputStyle}
          value={typed}
          onChange={(e) => { setTyped(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && confirmed) handleDelete();
            if (e.key === "Escape" && !busy) onClose();
          }}
          placeholder={target}
          aria-label="Confirmation"
        />

        {error && <div style={{ fontSize: 13, color: "var(--c-red-600)", marginTop: 12 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ ...btnBase, background: "transparent", color: "var(--color-text-secondary)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!confirmed || busy}
            style={{
              ...btnBase,
              background: "var(--c-red-600)",
              color: "#fff",
              opacity: !confirmed || busy ? 0.5 : 1,
              cursor: !confirmed || busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Deleting…" : `Delete ${entityKind}`}
          </button>
        </div>
      </div>
    </div>
  );
}
